import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

export interface Comment {
    _id: string;
    id: string;
    commentId: number;
    tokenId: string;
    content: string;
    author: {
        walletAddress: string;
    };
    parentId: string | null;
    isReply: boolean;
    likeCount: number;
    hasLiked?: boolean;
    status: string;
    timestamp: string;
    replyCount: number;
    replies?: Comment[];
}

interface CommentsState {
    comments: Comment[];
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    error: string | null;
    currentPage: number;
    hasMore: boolean;
    pagination?: {
        total: number;
        page: number;
        limit: number;
        totalPages: number;
        hasNextPage: boolean;
        hasPrevPage: boolean;
    };
}

const initialState: CommentsState = {
    comments: [],
    status: 'idle',
    error: null,
    currentPage: 1,
    hasMore: true
};

export const fetchComments = createAsyncThunk(
    'comments/fetchComments',
    async ({ tokenId, page = 1, limit = 10 }: { tokenId: string; page?: number; limit?: number }, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${process.env.NEXT_PUBLIC_API_URL}/comments/${tokenId}?limit=${limit}&page=${page}`);
            return {
                comments: response.data.comments,
                pagination: response.data.pagination,
                hasMore: response.data.pagination?.hasNextPage || false,
                isFirstPage: page === 1
            };
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || "Failed to fetch comments");
        }
    }
);

export const loadMoreComments = createAsyncThunk(
    'comments/loadMoreComments',
    async ({ tokenId, limit = 10 }: { tokenId: string; limit?: number }, { getState, rejectWithValue }) => {
        try {
            const state = getState() as { comments: CommentsState };
            const nextPage = (state.comments.pagination?.page || 1) + 1;

            const response = await axios.get(`${process.env.NEXT_PUBLIC_API_URL}/comments/${tokenId}?limit=${limit}&page=${nextPage}`);
            return {
                comments: response.data.comments,
                pagination: response.data.pagination,
                hasMore: response.data.pagination?.hasNextPage || false
            };
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || "Failed to load more comments");
        }
    }
);

export const createComment = createAsyncThunk(
    'comments/createComment',
    async (commentData: {
        tokenId: string;
        content: string;
        walletAddress: string;
        imageUrl?: string;
        parentId?: string | null;
    }, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${process.env.NEXT_PUBLIC_API_URL}/comments`, commentData);
            return response.data.comment;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || "Failed to create comment");
        }
    }
);

export const likeComment = createAsyncThunk(
    'comments/likeComment',
    async ({ tokenId, commentId }: { tokenId: string; commentId: string }, { rejectWithValue }) => {
        try {
            await axios.post(`${process.env.NEXT_PUBLIC_API_URL}/comments/${tokenId}/${commentId}/like`);
            return { commentId, liked: true };
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || "Failed to like comment");
        }
    }
);

export const unlikeComment = createAsyncThunk(
    'comments/unlikeComment',
    async ({ tokenId, commentId }: { tokenId: string; commentId: string }, { rejectWithValue }) => {
        try {
            await axios.post(`${process.env.NEXT_PUBLIC_API_URL}/comments/${tokenId}/${commentId}/unlike`);
            return { commentId, liked: false };
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || "Failed to unlike comment");
        }
    }
);

export const deleteComment = createAsyncThunk(
    'comments/deleteComment',
    async ({ tokenId, commentId, walletAddress }: { tokenId: string; commentId: string; walletAddress: string }, { rejectWithValue }) => {
        try {
            await axios.delete(`${process.env.NEXT_PUBLIC_API_URL}/comments/${tokenId}/${commentId}`, {
                data: {
                    walletAddress: walletAddress,
                    tokenId: tokenId
                }
            });
            return commentId;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || "Failed to delete comment");
        }
    }
);

const commentsSlice = createSlice({
    name: 'comments',
    initialState,
    reducers: {
        resetComments: (state) => {
            state.comments = [];
            state.currentPage = 1;
            state.hasMore = true;
            state.status = 'idle';
            state.pagination = undefined;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchComments.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchComments.fulfilled, (state, action) => {
                state.status = 'succeeded';
                const incomingPage = action.payload.pagination?.page || 1;

                if (action.payload.isFirstPage || incomingPage === 1) {
                    state.comments = action.payload.comments;
                    state.currentPage = 1;
                } else {
                    state.comments = [...state.comments, ...action.payload.comments];
                    state.currentPage = incomingPage;
                }

                state.hasMore = action.payload.hasMore;
                state.pagination = action.payload.pagination;
                state.error = null;
            })
            .addCase(fetchComments.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload as string;
            })

            .addCase(loadMoreComments.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(loadMoreComments.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.comments = [...state.comments, ...action.payload.comments];
                state.hasMore = action.payload.hasMore;
                state.pagination = action.payload.pagination;
                state.currentPage = action.payload.pagination?.page || state.currentPage;
                state.error = null;
            })
            .addCase(loadMoreComments.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload as string;
            })

            .addCase(createComment.fulfilled, (state, action) => {
                const newComment = action.payload;

                if (newComment.parentId) {
                    const parentComment = state.comments.find(comment => comment.id === newComment.parentId);
                    if (parentComment) {
                        parentComment.replies = [...(parentComment.replies || []), newComment];
                        parentComment.replyCount = (parentComment.replyCount || 0) + 1;
                    }
                } else {
                    state.comments.unshift(newComment);
                }
            })

            .addCase(likeComment.fulfilled, (state, action) => {
                const { commentId } = action.payload;
                const findAndUpdateComment = (comments: Comment[]) => {
                    for (const comment of comments) {
                        if (comment.id === commentId || comment._id === commentId || comment.commentId.toString() === commentId) {
                            comment.likeCount += 1;
                            comment.hasLiked = true;
                            return true;
                        }
                        if (comment.replies && comment.replies.length > 0) {
                            if (findAndUpdateComment(comment.replies)) return true;
                        }
                    }
                    return false;
                };

                findAndUpdateComment(state.comments);
            })

            .addCase(unlikeComment.fulfilled, (state, action) => {
                const { commentId } = action.payload;
                const findAndUpdateComment = (comments: Comment[]) => {
                    for (const comment of comments) {
                        if (comment.id === commentId || comment._id === commentId || comment.commentId.toString() === commentId) {
                            comment.likeCount = Math.max(0, comment.likeCount - 1);
                            comment.hasLiked = false;
                            return true;
                        }
                        if (comment.replies && comment.replies.length > 0) {
                            if (findAndUpdateComment(comment.replies)) return true;
                        }
                    }
                    return false;
                };

                findAndUpdateComment(state.comments);
            })

            .addCase(deleteComment.fulfilled, (state, action) => {
                const commentId = action.payload;
                state.comments = state.comments.filter(comment =>
                    comment.id !== commentId &&
                    comment._id !== commentId &&
                    comment.commentId.toString() !== commentId
                );

                const removeFromReplies = (comments: Comment[]) => {
                    for (const comment of comments) {
                        if (comment.replies && comment.replies.length > 0) {
                            const originalReplyCount = comment.replies.length;
                            comment.replies = comment.replies.filter(reply =>
                                reply.id !== commentId &&
                                reply._id !== commentId &&
                                reply.commentId.toString() !== commentId
                            );
                            if (comment.replies.length < originalReplyCount) {
                                comment.replyCount = Math.max(0, comment.replyCount - (originalReplyCount - comment.replies.length));
                            }
                            removeFromReplies(comment.replies);
                        }
                    }
                };

                removeFromReplies(state.comments);
            });
    },
});

export const { resetComments } = commentsSlice.actions;
export default commentsSlice.reducer; 